import React, { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { httpPost } from "../../../modules/Backend";
import { GroupedVirtuoso } from "react-virtuoso";
import { useParams, useLocation, useNavigate, useBlocker } from "react-router";
import Pill from "../Pill";
import { movingSidePanelSignal, staticSidePanelSignal } from "../layout/Layout";
import SidePanelRecallPickListItem from "../layout/SidePanel/layouts/SidePanelRecallPickListItem";
import { useSignals } from "@preact/signals-react/runtime";
import { FoundStatusSmartEnum } from "../../../enums/Domain/FoundStatusSmartEnum";
import GreenTick from "../../../assets/icons/icon-tick-green.svg";
import RedCross from "../../../assets/icons/icon-cross-red.svg";
import YellowQuestionMark from "../../../assets/icons/icon-decision-yellow.svg";
import SidePanelRecallPickList, { getFoundStatusFromQuantity } from "../layout/SidePanel/layouts/SidePanelRecallPickList";
import { getStatus } from "../../../enums/Domain/StatusLookup";
import { StatusDto } from "../../../enums/Domain/Status";
import { EntityNames } from "../../../enums/EntityNames";
import { modalSignal } from "../Modal/ModalContainer";
import UnsavedChangesModal from "../Modal/UnsavedChangesModal";
import useRecallPickListActions from "../picking/useRecallPickListActions";
import InputRadio from "../../lib/inputs/InputRadio";
import AdobeComboBox, { IOption } from "../../AdobeAriaLib/AdobeComboBox";

export interface IPickList {
  recallId: string;
  items: IPicklistItem[];
}

export interface IPicklistItem {
  id: string;
  breadcrumb: string;
  reference: string;
  type: string;
  intentDisplayValue: string;
  intent: string;
  status: StatusDto;
  foundStatus: StatusDto;
  notes: string;
  requestorComments: string;
  quantity: number | undefined | null;
  year: number;
}

const DEFAULT_NOT_FOUND_NOTES = 'NOT PRESENT NO INFORMATION';

export default function RecallsPickList() {
  useSignals();

  const { recallId } = useParams();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [lineItems, setLineItems] = useState<IPicklistItem[]>([]);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState<boolean>(false);
  const [groupedData, setGroupedData] = useState<{ breadcrumb: string; items: IPicklistItem[] }[]>([]);
  const [sortedItems, setSortedItems] = useState<IPicklistItem[]>([]);
  const [showLoader, setShowLoader] = useState<boolean>(false);
  const [viewedItem, setViewedItem] = useState<string>();
  const [groupByMode, setGroupByMode] = useState<"pallet" | "packingBox">("pallet");
  const [foundStatusFilter, setFoundStatusFilter] = useState<string>("All");
  const list = useRef<any>(null);
  const isInitialLoad = useRef<boolean>(true);

  // Use React Router's useBlocker to intercept navigation (now works with data router!)
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      hasUnsavedChanges && currentLocation.pathname !== nextLocation.pathname
  );

  const { save, submit } = useRecallPickListActions({
    recallId,
    items: lineItems
  });

  // Function to extract pallet breadcrumb (full breadcrumb minus the packing box segment)
  const getPalletBreadcrumb = (breadcrumb: string): string => {
    const segments = breadcrumb.split(" > ");
    if (segments.length >= 2) {
      // Return all segments except the last one (packing box)
      return segments.slice(0, segments.length - 1).join(" > ").trim();
    }
    return breadcrumb; // Fallback to full breadcrumb if not enough segments
  };

  // Function to filter items by found status
  const filterByFoundStatus = (items: IPicklistItem[]) => {
    if (foundStatusFilter === "All") {
      return items;
    }
    const filterValue = parseInt(foundStatusFilter);
    return items.filter((item: IPicklistItem) => {
      // Use foundStatus if available (it's kept in sync with quantity), otherwise derive from quantity
      const itemFoundStatus = item.foundStatus || getFoundStatusFromQuantity(item.quantity);
      return itemFoundStatus.value === filterValue;
    });
  };

  // Function to group data by breadcrumb or pallet and return sorted items
  const groupByBreadcrumb = (items: IPicklistItem[]) => {
    const groups = items.reduce((acc: { breadcrumb: string; items: IPicklistItem[] }[], item: IPicklistItem) => {
      const groupKey = groupByMode === "pallet" 
        ? getPalletBreadcrumb(item.breadcrumb)
        : item.breadcrumb;
      
      const group = acc.find((g: { breadcrumb: string; items: IPicklistItem[] }) => g.breadcrumb === groupKey);
      if (group) {
        group.items.push(item);
      } else {
        acc.push({ breadcrumb: groupKey, items: [item] });
      }
      return acc;
    }, []);
    
    // Flatten groups into a sorted array where items in the same group are consecutive
    const sorted = groups.flatMap((group: { breadcrumb: string; items: IPicklistItem[] }) => group.items);
    
    return { groups, sorted };
  };

  async function getData() {
    const timeout = setTimeout(() => setShowLoader(true), 200);
    const result = await httpPost(`api/${EntityNames.recalls}/getPickList`, { recallId: recallId });
    renderEmptyList();

    if (recallId && result?.data) {
      const items = result?.data.items;
      setLineItems(items);
      setHasUnsavedChanges(false);
      isInitialLoad.current = true;
      staticSidePanelSignal.value = <SidePanelRecallPickList recallId={recallId} items={items} />;
    }
    setShowLoader(false);
    clearTimeout(timeout);
  }


  const handleChange = (input: string) => (id: string, value: string | number | boolean | StatusDto) => {
    setLineItems((prevData: any) =>
      prevData.map((item: any) => {
        if (item.id === id) {
          const updatedItem = { ...item, [input]: value };
          
          // Auto-update notes and foundStatus based on quantity or foundStatus changes
          if (input === "quantity") {
            const quantity = value as number;
            // Update foundStatus to match the new quantity
            updatedItem.foundStatus = getFoundStatusFromQuantity(quantity);
            if (quantity === 0) {
              updatedItem.notes = DEFAULT_NOT_FOUND_NOTES;
            }
          } else if (input === "foundStatus") {
            const foundStatus = value as StatusDto;
            updatedItem.foundStatus = foundStatus;
            if (foundStatus.value === FoundStatusSmartEnum.NotFound.value) {
              updatedItem.notes = DEFAULT_NOT_FOUND_NOTES;
            }
          }
          
          return updatedItem;
        }
        return item;
      })
    );
  };

  // Save and clear unsaved changes flag
  const handleSave = useCallback(async () => {
    await save(lineItems);
    setHasUnsavedChanges(false);
    isInitialLoad.current = true; // Reset to prevent unsaved changes flag from being set if lineItems updates after save
  }, [save, lineItems]);

  // Submit and clear unsaved changes flag
  const handleSubmit = useCallback(async () => {
    await submit(lineItems);
    setHasUnsavedChanges(false);
    isInitialLoad.current = true; // Reset to prevent unsaved changes flag from being set if lineItems updates after submit
  }, [submit, lineItems]);


  function nextItem(currentItem: string) {
    const index = sortedItems.findIndex((x: any) => x.id === currentItem);
    return index === sortedItems.length - 1 ? undefined : () => {
      viewItem(sortedItems[index + 1]);
      scrollTo(index, "center");
    };
  }

  function prevItem(currentItem: string) {
    const index = sortedItems.findIndex((x: any) => x.id === currentItem);
    return index === 0 ? undefined : () => {
      viewItem(sortedItems[index - 1]);
      scrollTo(index, "center");
    };
  }

  const scrollTo = (index: number, align: "start" | "end" | "center") => {
    list.current!.scrollToIndex({
      index: index,
      align: align,
      behavior: "smooth",
    });
  };

  function viewItem(lineItem: any) {
    movingSidePanelSignal.value = {
      show: true,
      id: lineItem.id,
      component: (
        <SidePanelRecallPickListItem
          recallId={recallId}
          selectedItem={lineItem}
          items={lineItems}
          handleChange={handleChange}
          nextItem={nextItem(lineItem.id)}
          prevItem={prevItem(lineItem.id)}
          onSave={handleSave}
          onSubmit={handleSubmit}
        />
      ),
    };
  }

  // Call getData on component mount
  useEffect(() => {
    getData();
  }, []);


  // Handle browser beforeunload
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = '';
        return '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [hasUnsavedChanges]);

  // Handle blocked navigation from useBlocker
  useEffect(() => {
    if (blocker.state === 'blocked') {
      modalSignal.value = <UnsavedChangesModal
        onDiscard={() => {
          blocker.proceed();
        }}
        onSave={async () => {
          await handleSave();
          blocker.proceed();
        }}
        onCancel={() => {
          blocker.reset();
        }}
      />;
    }
  }, [blocker, handleSave]);

  // Create options for the found status dropdown
  const foundStatusOptions: IOption[] = useMemo(() => [
    { text: "All", value: "All" },
    { text: FoundStatusSmartEnum.Undecided.displayValue, value: FoundStatusSmartEnum.Undecided.value.toString() },
    { text: FoundStatusSmartEnum.NotFound.displayValue, value: FoundStatusSmartEnum.NotFound.value.toString() },
    { text: FoundStatusSmartEnum.Confirmed.displayValue, value: FoundStatusSmartEnum.Confirmed.value.toString() },
  ], []);

  // Calculate filtered items count
  const filteredItems = useMemo(() => filterByFoundStatus(lineItems), [lineItems, foundStatusFilter]);
  const filteredCount = filteredItems.length;
  const totalCount = lineItems.length;

  // Group data by breadcrumb whenever data changes or groupByMode/filter changes (display logic only)
  useEffect(() => {
    if (lineItems.length > 0) {
      const { groups, sorted } = groupByBreadcrumb(filteredItems);
      setGroupedData(groups);
      setSortedItems(sorted);
    } else {
      setGroupedData([]);
      setSortedItems([]);
    }
    if (viewedItem) {
      viewItem(lineItems.find((x: any) => x.id == viewedItem));
    }
    staticSidePanelSignal.value = <SidePanelRecallPickList recallId={recallId!} items={lineItems} />;
  }, [lineItems, groupByMode, foundStatusFilter, filteredItems]);

  // Set unsaved changes flag when lineItems actually change (but not on initial load or filter/group changes)
  useEffect(() => {
    // Set unsaved changes flag when lineItems change (but not on initial load)
    if (!isInitialLoad.current) {
      setHasUnsavedChanges(true);
    } else {
      isInitialLoad.current = false;
    }
  }, [lineItems]);

  useEffect(() => {
    if (movingSidePanelSignal.value.id && movingSidePanelSignal.value.show) {
      setViewedItem(movingSidePanelSignal.value.id);
    } else setViewedItem("");
  }, [movingSidePanelSignal.value]);

  function renderEmptyList() {
    return (
      <div>
        {showLoader && lineItems.length == 0 &&
          <div className="react-aria-Table">
            <table>
              <th><p className="small">Reference</p></th>
              <th><p className="small">Contents</p></th>
              <th><p className="small">Type</p></th>
              <th><p className="small">Year</p></th>
              <th><p className="small">Status</p></th>
              {[...Array(5)].map((e, i) => (
                <tr>
                  {[...Array(5)].map((e, i) => (
                    <td>
                      <div className="loaders loaders__list"></div>
                    </td>
                  ))}
                </tr>
              ))}
            </table>
          </div>
        }
      </div>
    )
  }

  function renderFoundStatus(quantity: number | undefined | null) {
    const statusDto = getFoundStatusFromQuantity(quantity);
    const status = getStatus(statusDto);
    switch (statusDto.value) {
      case FoundStatusSmartEnum.Confirmed.value:
        return (
          <>
            <img src={GreenTick} />
            <p className="small">{status?.displayValue}</p>
          </>
        );
      case FoundStatusSmartEnum.NotFound.value:
        return (
          <>
            <img src={RedCross} />
            <p className="small">{status?.displayValue}</p>
          </>
        );
      case FoundStatusSmartEnum.Undecided.value:
        return (
          <>
            <img src={YellowQuestionMark} />
            <p className="small">{status?.displayValue}</p>
          </>
        );
    }
  }

  const handleGroupByChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setGroupByMode(e.target.value as "pallet" | "packingBox");
  };

  const handleFoundStatusFilterChange = (selected: string | null) => {
    setFoundStatusFilter(selected || "All");
    // Deselect the currently selected item and close the side panel
    setViewedItem(undefined);
    movingSidePanelSignal.value = { ...movingSidePanelSignal.value, show: false };
  };

  return (
    <>
      {renderEmptyList()}
      {!showLoader &&
        <div className="height--full list__wrapper">
          <div className="padding--xxs margin__bottom--xxs display--flex flex__direction--row justifyContent--space-between alignItems--center gap--xs">
            <div className="display--flex flex__direction--row alignItems--center gap--xxs">
              <label className="small--bold" htmlFor="foundStatusFilter">
                Filter by
              </label>
              <AdobeComboBox
                value={foundStatusFilter}
                label="Filter by Found Status"
                options={foundStatusOptions}
                onChangeSelect={handleFoundStatusFilterChange}
                disabled={false}
                labelVisible={false}
                required={false}
              />
              <div className="display--flex flex__direction--column">
                <p className="xsmall">
                  Showing:
                </p>
                <p className="small">
                  {filteredCount} / {totalCount}
                </p>
              </div>
            </div>
            <div className="display--flex flex__direction--row alignItems--center gap--xs">
              <label className="small--bold" htmlFor="groupByMode">
                Group by
              </label>
              <div className="display--flex flex__direction--row gap--xxs">
                <InputRadio
                  id="groupByPallet"
                  name="groupByMode"
                  value="pallet"
                  label="Pallet"
                  checked={groupByMode === "pallet"}
                  onChangeCheckBox={handleGroupByChange}
                />
                <InputRadio
                  id="groupByPackingBox"
                  name="groupByMode"
                  value="packingBox"
                  label="Packing Box"
                  checked={groupByMode === "packingBox"}
                  onChangeCheckBox={handleGroupByChange}
                />
              </div>
            </div>
          </div>
          <div className="grid-x padding--xxs alignItems--center">
            <p className="small small-2">Reference</p>
            <p className="small small-2">Contents</p>
            <p className="small small-2">Type</p>
            <p className="small small-2">Year</p>
            <p className="small small-2">Status</p>
          </div>
          <GroupedVirtuoso
            groupCounts={groupedData.map((group) => group.items.length)}
            groupContent={(index) => (
              <div className="pick-list__group-header-wrapper">
                <p className="pick-list__group-header width--full breadcrumb">{groupedData[index].breadcrumb}</p>
              </div>
            )}
            ref={list}
            itemContent={(index) => {
              const lineItem = sortedItems[index];
              if (!lineItem) return null; // Safeguard against undefined items

              return (
                <div className="pick-list__item" onClick={() => viewItem(lineItem)} data-selected={viewedItem === lineItem.id}>
                  <div className="grid-x padding--xxs alignItems--center">
                    <div className="small-2">
                      <p className="textwrap">{lineItem.reference}</p>
                    </div>
                    <div className="small-2">
                      <p className="textwrap">{lineItem.intentDisplayValue}</p>
                    </div>
                    <div className="small-2">
                      <p className="textwrap">{lineItem.type}</p>
                    </div>
                    <div className="small-2">
                      <p className="textwrap">{lineItem.year != 0 ? lineItem.year : '--'}</p>
                    </div>
                    <div className="small-2">
                      <Pill statusDto={lineItem.status} />
                    </div>
                    <div className="small-2 display--flex justifyContent--flex-end alignItems--center gap--xxxs">
                      {renderFoundStatus(lineItem.quantity)}
                    </div>
                  </div>
                </div>
              );
            }}
          />
        </div>
      }
    </>
  );
}
